import { useMemo } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOfferList from 'hooks/Offers/useOfferList'
import { ELIGIBLE_AD_GROUP_IDS } from 'constants/salesCtrRankingAdGroups'
import Places from 'constants/places'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
} from 'constants/offer'

const TOP_N = 5
const TOP_N_COMPARISON = 3
const HOTEL_OFFER_TYPES: Array<App.OfferListOfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
]
const BALI_DESTINATION_ID = Places.Bali.id

export function hasTopOffersChanged(idsA: Array<string>, idsB: Array<string>): boolean {
  const topA = idsA.slice(0, TOP_N_COMPARISON)
  const topB = idsB.slice(0, TOP_N_COMPARISON)

  if (topA.length === 0 || topB.length === 0) return false

  return topA.some((id, index) => id !== topB[index])
}

interface UseSalesAndCtrRankingResult {
  experimentVariant: string | undefined
  rankingComparisonPending: boolean
}

function useSalesAndCtrRanking(destinationId: string | undefined): UseSalesAndCtrRankingResult {
  const query = useQueryParams()
  const utmAdgroup = query.get('utm_adgroup')

  const isEligibleAdgroup = !!utmAdgroup && ELIGIBLE_AD_GROUP_IDS.has(utmAdgroup)
  const isBali = destinationId === BALI_DESTINATION_ID
  const shouldCompare = isEligibleAdgroup && !!destinationId && !isBali

  const baseFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!shouldCompare) return undefined
    return {
      destinationId,
      offerTypes: HOTEL_OFFER_TYPES,
      limit: TOP_N,
    }
  }, [shouldCompare, destinationId])

  const filtersWithRollout = useMemo((): App.OfferListFilters | undefined => {
    if (!baseFilters) return undefined
    return {
      ...baseFilters,
      rollout: ['salesCtrRanking'],
    }
  }, [baseFilters])

  const withRolloutList = useOfferList(filtersWithRollout, { disabled: !shouldCompare })
  const withoutRolloutList = useOfferList(baseFilters, { disabled: !shouldCompare })

  const rankingComparisonPending = shouldCompare && (withRolloutList.fetching || withoutRolloutList.fetching)

  const rankingsDiffer = useMemo(() => {
    if (!shouldCompare || rankingComparisonPending) return false

    return hasTopOffersChanged(withRolloutList.offerIds, withoutRolloutList.offerIds)
  }, [shouldCompare, rankingComparisonPending, withRolloutList.offerIds, withoutRolloutList.offerIds])

  const canActivate = isEligibleAdgroup && !rankingComparisonPending && rankingsDiffer

  const experimentVariant = useOptimizelyExperiment(OptimizelyExperiments.searchSalesCtrPaidTrafficRanking, canActivate)

  return { experimentVariant, rankingComparisonPending }
}

export default useSalesAndCtrRanking
